<template>
<aside class="sidebar">
  <div
    :class="['sidebar__backdrop', {
      'sidebar__backdrop_active': isSidebarActive
    }]"
  ></div>
  <div class="sidebar__mobile-header">
    <button
      type="button"
      class="sidebar__burger"
      :aria-label="!isSidebarActive ? $t('a11y.openDropdown') : $t('a11y.closeDropdown')"
      @click="toggleMobileSidebar"
      id="nav-menu-button"
      aria-haspopup="true"
      :aria-expanded="String(isSidebarActive)"
    >
      <KonturIcon name="UiMenuBarsThreeHIcon" size="24" />
    </button>
    <router-link to="/" :aria-label="$t('a11y.goToHomePage')">
      <img
        src="@/assets/images/logo/kontur_logo.svg"
        alt="logo"
        class="sidebar__logo"
      >
    </router-link>
  </div>
  <div
    :class="['sidebar__content', {
      'sidebar__content_active-on-mobile': isSidebarActive
    }]"
  >
    <header class="sidebar__header">
      <router-link to="/" :aria-label="$t('a11y.goToHomePage')">
        <img
          src="@/assets/images/logo/kontur_logo.svg"
          alt="logo"
          class="sidebar__logo"
        >
      </router-link>
    </header>
    <nav
      class="sidebar__nav"
      aria-labelledby="nav-menu-button"
      ref="menu"
      role="navigation"
    >
      <ul class="sidebar__menu">
        <li
          v-for="(sidebarItem, index) in sidebarItems"
          :key="`sidebarItem-${index}`"
          class="sidebar__menu-item"
        >
          <SidebarGroup
            v-if="sidebarItem.submenu"
            :group="sidebarItem"
          />
          <SidebarItem
            v-else
            :to="sidebarItem.url"
            :href="sidebarItem.externalUrl"
            :icon="sidebarItem.icon"
            :target="sidebarItem.target"
            :isDisabled="sidebarItem.isDisabled"
            :tag="sidebarItem.tag"
          >
            {{ $t(sidebarItem.name_i18n) || sidebarItem.name }}
          </SidebarItem>
        </li>
      </ul>
      <ul class="sidebar__menu sidebar__menu_footer">
        <li class="sidebar__menu-item">
          <SidebarItem
            to="/branch/add"
            icon="PlusIcon"
          >
            {{ $t('pages.newBranch') }}
          </SidebarItem>
        </li>
        <li class="sidebar__menu-item">
          <!--
            ToDo a11y fix.
            https://www.w3.org/WAI/ARIA/apg/example-index/menu-button/menu-button-links.html
          -->
          <vs-dropdown vs-trigger-click>
            <SidebarItem icon="PeopleOneIcon" :textEllipsis="true">
              {{ currentUser.first_name || currentUser.email || $t('navigation.user') }}
            </SidebarItem>
            <vs-dropdown-menu>
              <vs-dropdown-item href="https://cabinet.kontur.ru/" target="_blank" @click="handleTrack('Cabinet')">
                {{ $t('navigation.userAccount') }}
              </vs-dropdown-item>
              <vs-dropdown-item divider to='/logout' @click:native="handleTrack('Quit')">
                {{ $t('navigation.logOut') }}
              </vs-dropdown-item>
            </vs-dropdown-menu>
          </vs-dropdown>
        </li>
      </ul>
    </nav>
  </div>
</aside>
</template>

<script>
import SidebarGroup from './SidebarGroup'
import SidebarItem from './SidebarItem'
import KonturIcon from '@/components/KonturIcon'
import { mapGetters } from 'vuex'

export default {
  name: 'TheSidebar',
  components: {
    SidebarGroup,
    SidebarItem,
    KonturIcon
  },
  props: {
    sidebarItems: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isSidebarActive: false
    }
  },
  computed: {
    ...mapGetters([
      'currentUser'
    ]),
    getUserEmail() {
      return this.currentUser.email ? this.currentUser.email : ''
    },
    getUserName() {
      return this.currentUser.first_name
    }
  },
  watch: {
    '$route'() {
      this.isSidebarActive = false
    }
  },
  methods: {
    toggleMobileSidebar() {
      if (!this.isSidebarActive) {
        this.isSidebarActive = true
      } else {
        this.isSidebarActive = false
      }
    },
    handleTrack(label) {
      this.$km_track('Menu', 'Click', label)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuesax/_variables.scss';

$sidebar-width: $module * 31;  // 248px

.sidebar {
  flex-shrink: 0;
  width: $sidebar-width;
  position: relative;
  z-index: 10;
}

.sidebar__mobile-header {
  display: none;
}

.sidebar__content {
  position: fixed;
  top: 0;
  left: 0;
  width: $sidebar-width;
  height: 100%;
  background-color: $sidebar-bg-color;
  padding: 0;
  font-size: $font-size-base;
  line-height: 1.375;
  display: flex;
  flex-direction: column;
}

.sidebar__header {
  padding: #{$size-3 + $size-half} $size-2 $size-2;
  box-shadow: 0 4px 2px -2px $sidebar-bg-color;
  position: relative;
  z-index: 1;
}

.sidebar__burger {
  padding: 0;
  margin: 0;
  background-color: transparent;
  width: $size-5;
  height: $size-5;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  margin-right: $size-2;
  cursor: pointer;

  &:focus-visible {
    box-shadow: 0 0 0 2px inset rgb(28, 126, 223);
    background-color: $white;
    outline: none;
  }
}

.sidebar__logo {
  display: block;
}

.sidebar__nav {
  padding: 0 $size-2 #{$size-2 + $size-half};
  margin: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    width: $size-half;
  }

  &::-webkit-scrollbar-track {
    border-radius: $size-half;
    background: rgba(0,0,0,0.05);
  }

  &::-webkit-scrollbar-thumb {
    background-color: $scrollbar-color;
    border-radius: $size-half;
    width: $size-half;
  }

  & {
    scrollbar-width: thin;
    scrollbar-color: $scrollbar-color;
  }
}

.sidebar__menu {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.sidebar__menu-item {
  list-style-type: none;
  padding: 0;
  margin: 0;
  font-size: $font-size-base;
}

.sidebar__menu_footer {
  margin-top: auto;
}

.sidebar__menu-item::v-deep {
  .vs-con-dropdown {
    width: 100%;
    cursor: pointer;
    border-radius: $size-1;

    &:hover .sidebar-item {
      background-color: rgba(255, 255, 255, 0.48);
    }

    &:focus-visible {
      box-shadow: 0 0 0 2px inset rgb(28, 126, 223);
      background-color: $white;
      outline: none;
    }
  }
}

@media only screen and (max-width: $sidebar-visibility-breakpoint) {
  $sidebar-width: $module * 39;

  .sidebar {
    width: 100%;
    padding: 0;
    background-color: $white;
    position: sticky;
    box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.04);
    top: 0;
    z-index: 10;
  }

  .sidebar__backdrop {
    position: fixed;
    z-index: -1;
    width: 0;
    height: 0;
    top: 0;
    left: 0;
    background: rgba(0, 0, 0, 0.16);
    opacity: 0;
    transition: opacity .150s;

    &_active {
      width: 100%;
      height: 100%;
      opacity: 1;
    }
  }

  .sidebar__mobile-header {
    display: flex;
    align-items: center;
    width: 100%;
    padding: $size-1 $size-2 $size-1;
    position: relative;
    z-index: 2;
  }

  .sidebar__header {
    visibility: hidden;
  }

  .sidebar__content {
    width: $sidebar-width;
    transform: translateX(-$sidebar-width);
    transition: transform .150s;
  }

  .sidebar__content_active-on-mobile {
    transform: translateX(0);
    z-index: 1;
  }
}
</style>

